/**
 * SignoffsPanel - Phase 2 Sign-offs and E-signatures
 * Request sign-offs, view audit trail, manage e-sign requests
 */

import React, { useState, useEffect } from 'react';
import {
  Shield, FileCheck, Clock, User, Check, X, Send, AlertTriangle,
  ChevronDown, ChevronRight, Plus, RefreshCw, ExternalLink, Pencil
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { toast } from 'sonner';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Signoff types
const SIGNOFF_TYPES = [
  { value: 'PMO', label: 'PMO Approval', color: 'bg-blue-100 text-blue-700' },
  { value: 'SteerCo', label: 'Steering Committee', color: 'bg-purple-100 text-purple-700' },
  { value: 'Compliance', label: 'Compliance', color: 'bg-green-100 text-green-700' },
  { value: 'Legal', label: 'Legal Review', color: 'bg-amber-100 text-amber-700' },
  { value: 'Ops', label: 'Operations', color: 'bg-gray-100 text-gray-700' },
];

// E-sign providers
const ESIGN_PROVIDERS = [
  { value: 'mock', label: 'Mock (Testing)', description: 'Simulated e-sign for testing' },
  { value: 'docusign', label: 'DocuSign', description: 'DocuSign integration' },
  { value: 'adobesign', label: 'Adobe Sign', description: 'Adobe Sign integration' },
  { value: 'onespan', label: 'OneSpan', description: 'OneSpan Sign integration' },
];

// Status chips
const STATUS_COLORS = {
  sent: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  declined: 'bg-red-100 text-red-700 border-red-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
};

/**
 * Request Sign-off Modal
 */
const RequestSignoffModal = ({ isOpen, onClose, scopeId, tenantId, onSuccess }) => {
  const { user } = useAuth();
  const [signoffType, setSignoffType] = useState('PMO');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleRequest = async () => {
    setLoading(true);
    try {
      await api.createSignoff(
        {
          type: signoffType,
          relates_to: 'scopeNode',
          relates_to_id: scopeId,
          comment: comment.trim() || undefined
        },
        tenantId,
        user?.id || user?._id,
        user?.name || user?.email
      );
      
      trackEvent(TELEMETRY_EVENTS.SCOPING_SIGNOFF_REQUESTED, {
        scopeId,
        type: signoffType
      });
      
      toast.success('Sign-off recorded');
      onSuccess?.();
      onClose();
      setComment('');
    } catch (err) {
      toast.error('Failed to create sign-off');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            Request Sign-off
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-primary)] mb-1">
              Sign-off Type
            </label>
            <Select value={signoffType} onValueChange={setSignoffType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SIGNOFF_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-primary)] mb-1">
              Comment (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Additional notes..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-[var(--clr-border-base)] rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]/20"
            />
          </div>
          
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleRequest}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Record Sign-off
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)] rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * E-Sign Request Modal
 */
const EsignRequestModal = ({ isOpen, onClose, scopeId, docUrl, tenantId, onSuccess }) => {
  const { user } = useAuth();
  const [provider, setProvider] = useState('mock');
  const [signoffType, setSignoffType] = useState('PMO');
  const [recipients, setRecipients] = useState([{ email: '', name: '', role: 'Signer' }]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  const addRecipient = () => {
    setRecipients([...recipients, { email: '', name: '', role: 'Signer' }]);
  };
  
  const updateRecipient = (idx, field, value) => {
    const updated = [...recipients];
    updated[idx][field] = value;
    setRecipients(updated);
  };
  
  const removeRecipient = (idx) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== idx));
    }
  };
  
  const handleSend = async () => {
    const validRecipients = recipients.filter(r => r.email.trim());
    if (validRecipients.length === 0) {
      toast.error('At least one recipient email is required');
      return;
    }
    
    setLoading(true);
    try {
      const result = await api.requestEsign(
        {
          provider,
          recipients: validRecipients,
          doc_url: docUrl,
          scope_id: scopeId,
          signoff_type: signoffType,
          message: message.trim() || undefined
        },
        tenantId,
        user?.id || user?._id,
        user?.name || user?.email
      );
      
      trackEvent(TELEMETRY_EVENTS.SCOPING_ESIGN_REQUEST_SENT, {
        scopeId,
        provider,
        recipientCount: validRecipients.length
      });
      
      toast.success(`E-sign request sent via ${provider}`);
      
      // For mock provider, simulate completion after 3 seconds
      if (provider === 'mock') {
        setTimeout(async () => {
          try {
            await api.simulateEsignWebhook('mock', result.data.envelope_id, 'completed');
            toast.success('Mock e-sign completed');
            onSuccess?.();
          } catch (e) {
            console.error('Mock webhook error:', e);
          }
        }, 3000);
      }
      
      onSuccess?.();
      onClose();
      setRecipients([{ email: '', name: '', role: 'Signer' }]);
      setMessage('');
    } catch (err) {
      toast.error('Failed to send e-sign request');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            Request E-Signature
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-primary)] mb-1">
                Provider
              </label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ESIGN_PROVIDERS.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--clr-text-primary)] mb-1">
                Sign-off Type
              </label>
              <Select value={signoffType} onValueChange={setSignoffType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SIGNOFF_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[var(--clr-text-primary)]">
                Recipients
              </label>
              <button
                onClick={addRecipient}
                className="text-xs text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {recipients.map((r, idx) => (
                <div key={idx} className="flex gap-2 items-start">
                  <input
                    type="email"
                    placeholder="Email"
                    value={r.email}
                    onChange={(e) => updateRecipient(idx, 'email', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border rounded"
                  />
                  <input
                    type="text"
                    placeholder="Name"
                    value={r.name}
                    onChange={(e) => updateRecipient(idx, 'name', e.target.value)}
                    className="w-24 px-2 py-1.5 text-sm border rounded"
                  />
                  {recipients.length > 1 && (
                    <button
                      onClick={() => removeRecipient(idx)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[var(--clr-text-primary)] mb-1">
              Message (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please review and sign..."
              rows={2}
              className="w-full px-3 py-2 text-sm border rounded-lg"
            />
          </div>
          
          {provider === 'mock' && (
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              Mock provider will auto-complete after 3 seconds for testing.
            </div>
          )}
          
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSend}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send Request
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)] rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Signoffs Panel - Main Component
 */
const SignoffsPanel = ({ scopeId, tenantId, canEdit = true }) => {
  const [signoffs, setSignoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showSignoffModal, setShowSignoffModal] = useState(false);
  const [showEsignModal, setShowEsignModal] = useState(false);
  const { getFeature } = useProductMode();
  
  const signoffsEnabled = getFeature('scoping.signoffs', false);
  const esignEnabled = getFeature('scoping.esign', false);
  
  useEffect(() => {
    if (scopeId && tenantId && signoffsEnabled) {
      loadSignoffs();
    } else {
      setLoading(false);
    }
  }, [scopeId, tenantId, signoffsEnabled]);
  
  const loadSignoffs = async () => {
    setLoading(true);
    try {
      const res = await api.getSignoffs(tenantId, { scopeId });
      setSignoffs(res.data || []);
    } catch (err) {
      console.error('Failed to load signoffs:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (!signoffsEnabled) return null;
  
  const completedCount = signoffs.filter(s => !s.esign_status || s.esign_status === 'completed').length;
  const pendingCount = signoffs.filter(s => s.esign_status === 'sent').length;
  
  return (
    <div className="border border-[var(--clr-border-light)] rounded-lg overflow-hidden" data-testid="signoffs-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-[var(--clr-surface-subtle)] hover:bg-[var(--clr-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-green-600" />
          <span className="text-sm font-medium">Sign-offs</span>
          {signoffs.length > 0 && (
            <>
              <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                {completedCount}
              </span>
              {pendingCount > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                  {pendingCount} pending
                </span>
              )}
            </>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="p-3 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-4 h-4 text-[var(--clr-text-muted)] animate-spin" />
            </div>
          ) : signoffs.length === 0 ? (
            <p className="text-xs text-[var(--clr-text-muted)] text-center py-2">
              No sign-offs recorded yet
            </p>
          ) : (
            signoffs.map((signoff) => {
              const typeInfo = SIGNOFF_TYPES.find(t => t.value === signoff.type) || SIGNOFF_TYPES[0];
              return (
                <div
                  key={signoff.id}
                  className="flex items-center justify-between p-2 bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 text-xs rounded ${typeInfo.color}`}>
                        {signoff.type}
                      </span>
                      {signoff.esign_status && (
                        <span className={`px-1.5 py-0.5 text-xs rounded border ${STATUS_COLORS[signoff.esign_status]}`}>
                          {signoff.esign_status}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-[var(--clr-text-muted)]">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {signoff.actor_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(signoff.signed_at).toLocaleString()}
                      </span>
                    </div>
                    {signoff.comment && (
                      <p className="text-xs text-[var(--clr-text-secondary)] mt-1 italic">
                        "{signoff.comment}"
                      </p>
                    )}
                  </div>
                  {signoff.esign_status === 'completed' && (
                    <Check className="w-5 h-5 text-green-600" />
                  )}
                </div>
              );
            })
          )}
          
          {canEdit && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setShowSignoffModal(true)}
                className="flex-1 flex items-center justify-center gap-1 p-2 text-xs text-[var(--clr-brand-primary)] hover:bg-[var(--clr-surface-hover)] rounded-lg transition-colors"
              >
                <Plus className="w-3 h-3" />
                Record Sign-off
              </button>
              {esignEnabled && (
                <button
                  onClick={() => setShowEsignModal(true)}
                  className="flex-1 flex items-center justify-center gap-1 p-2 text-xs text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                >
                  <Send className="w-3 h-3" />
                  Request E-Sign
                </button>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Modals */}
      <RequestSignoffModal
        isOpen={showSignoffModal}
        onClose={() => setShowSignoffModal(false)}
        scopeId={scopeId}
        tenantId={tenantId}
        onSuccess={loadSignoffs}
      />
      
      <EsignRequestModal
        isOpen={showEsignModal}
        onClose={() => setShowEsignModal(false)}
        scopeId={scopeId}
        tenantId={tenantId}
        onSuccess={loadSignoffs}
      />
    </div>
  );
};

export default SignoffsPanel;
export { RequestSignoffModal, EsignRequestModal };
